// attempt-finished-worker-decisions.ts — V2-1 E13 (spec V2 §3 + §4.1 "Thay
// đổi pack", plan phase-03 B10, Luke chốt D2 = X 06/10).
//
// `result.decisions[]` của worker (khai ở autopilot-result-schema.ts, trước
// đây 0 reader) đi vào journal dưới dạng field `worker_decisions` trên CHÍNH
// event `attempt_finished` của attempt đó — cùng khuôn `uncovered_areas`
// (supervisor-loop-outcome-journal.ts), không thêm kind. Mảng rỗng/vắng/sai
// kiểu ⇒ `undefined` ⇒ không gắn field ⇒ payload byte-identical baseline
// (golden I1 (c)). Chuỗi tự do của worker qua redactor (pattern + known-values
// HIỆN TẠI caller lấy theo mission lúc finalize — Codex F9: che toàn text RỒI
// mới cắt §5.4), đệ quy, giữ nguyên cấu trúc record.
// Review 03b L3: tối đa 20 record, độ sâu 5 — cắt ⇒ thêm
// `worker_decisions_truncated: true`; lỗi khi đọc ⇒ bỏ field (attempt_finished
// vẫn ghi). Sổ quyết định đọc field này thành mục "worker ghi nhận" (KHÔNG phải
// quyết định auto/người — decision-ledger-projection.ts).
import { redactDecisionText } from "./decision-policy-journal-events.js";

export const WORKER_DECISIONS_MAX_RECORDS = 20;
export const WORKER_DECISIONS_MAX_DEPTH = 5;
const DEPTH_CUT_MARK = "[cắt: quá độ sâu]";

export interface WorkerDecisionsJournalFields {
  worker_decisions: Array<Record<string, unknown>>;
  worker_decisions_truncated?: true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Redact đệ quy; object/array nằm sâu hơn trần ⇒ thay bằng dấu cắt và báo `cut`. */
function redactDeep(
  value: unknown,
  depth: number,
  cut: { hit: boolean },
  secretValues: readonly string[],
): unknown {
  if (typeof value === "string") return redactDecisionText(value, secretValues);
  if (value === null || typeof value !== "object") return value;
  if (depth >= WORKER_DECISIONS_MAX_DEPTH) {
    cut.hit = true;
    return DEPTH_CUT_MARK;
  }
  if (Array.isArray(value)) return value.map((v) => redactDeep(v, depth + 1, cut, secretValues));
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([k, v]) => [
      k,
      redactDeep(v, depth + 1, cut, secretValues),
    ]),
  );
}

/** `undefined` khi worker không gửi quyết định nào (vắng, `[]`, sai kiểu) hoặc đọc lỗi. */
export function extractWorkerDecisionsFromResult(
  result: unknown,
  secretValues: readonly string[],
): WorkerDecisionsJournalFields | undefined {
  try {
    if (!isRecord(result) || !Array.isArray(result.decisions)) return undefined;
    const records = result.decisions.filter(isRecord);
    if (records.length === 0) return undefined;
    const cut = { hit: records.length > WORKER_DECISIONS_MAX_RECORDS };
    const kept = records
      .slice(0, WORKER_DECISIONS_MAX_RECORDS)
      .map((d) => redactDeep(d, 1, cut, secretValues) as Record<string, unknown>);
    return { worker_decisions: kept, ...(cut.hit ? { worker_decisions_truncated: true } : {}) };
  } catch {
    // Ghi nhận của worker là phụ — không bao giờ làm hỏng attempt_finished.
    return undefined;
  }
}
